import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { PartnersNextStepResponse } from '../../dtos/response/applicantResponse';
import {
    ApplicantReasonCodeResponse,
    ApplicantTagResponse,
    getApplicant,
    getApplicantActivity,
    getApplicantDocuments,
    getApplicantDueDiligence,
    getApplicantMissingDocuments,
    getApplicantNextSteps,
    getApplicantPointOfContacts,
    getApplicantReasonCodes,
    getApplicantRelatedAccounts,
    getApplicants,
    GetApplicantsFilter,
    getApplicantSubmission,
    getApplicantSubmissions,
    getApplicantTags,
    getApplicantVendorStatus,
    getApplicantWatchlistMatches,
    MissingDocumentsResponse,
    WatchlistMatchesResponse,
} from '../endpoints/applicant';
import { getAssignees } from '../endpoints/applicantAssignees';
import {
    getRecentNeedsMoreInfoSent,
    RecentNeedsMoreInfoSentRow,
} from '../endpoints/applicantInformationRequests';
import { getOrpUserProfiles, OrpUserProfile } from '../endpoints/slack';

import {
    Applicant,
    ApplicantActivityResponse,
    ApplicantDueDiligenceData,
    Document,
    PointOfContact,
    RelatedAccountsResponse,
} from '../../../pages/Applicant/Applicant.types';
import { ApplicantSubmissionSummary } from '../../../pages/Applicant/components/ApplicantSubmissions/ApplicantSubmissions.types';
import { VendorStatusResponse } from '../../../pages/Applicant/components/VendorStatusTable/VendorStatusTable.types';
import { FOCUS_STALE_TIME_MS, MISSING_DOCUMENTS_STALE_TIME_MS } from './staleTime';

interface GetApplicantQueryProps {
    applicantId: string;
    retry?: number;
    enabled?: boolean;
    refetchInterval?: number | false;
}

export const useApplicantQuery = ({
    applicantId,
    retry = 3,
    enabled = true,
    refetchInterval = false,
}: GetApplicantQueryProps): UseQueryResult<Applicant, AxiosError> =>
    useQuery({
        queryKey: ['applicant', applicantId],
        enabled: Boolean(applicantId) && enabled,
        // Fresh until the next 30 s poll, so refocusing the window right after a fetch doesn't refetch.
        staleTime: 30_000,
        retry,
        refetchInterval,
        refetchIntervalInBackground: false,
        queryFn: async () => {
            return await getApplicant(applicantId);
        },
    });

export const useApplicantActivityQuery = ({
    applicantId,
    retry = 3,
    enabled = true,
    refetchInterval = false,
}: GetApplicantQueryProps): UseQueryResult<ApplicantActivityResponse, AxiosError> =>
    useQuery({
        queryKey: ['applicant-activity', applicantId],
        staleTime: FOCUS_STALE_TIME_MS,
        enabled: Boolean(applicantId) && enabled,
        retry,
        refetchInterval,
        refetchIntervalInBackground: false,
        queryFn: async () => {
            return await getApplicantActivity(applicantId);
        },
    });

export const useApplicantDueDiligenceQuery = ({
    applicantId,
}: GetApplicantQueryProps): UseQueryResult<ApplicantDueDiligenceData[]> =>
    useQuery({
        queryKey: ['applicant-due-diligence', applicantId],
        staleTime: FOCUS_STALE_TIME_MS,
        enabled: Boolean(applicantId),
        queryFn: async () => {
            return await getApplicantDueDiligence(applicantId);
        },
    });

export const useApplicantVendorStatusQuery = ({
    applicantId,
    refetchInterval,
}: {
    applicantId: string;
    refetchInterval?: UseQueryOptions<VendorStatusResponse>['refetchInterval'];
}): UseQueryResult<VendorStatusResponse> =>
    useQuery({
        queryKey: ['applicant-vendor-status', applicantId],
        staleTime: FOCUS_STALE_TIME_MS,
        enabled: Boolean(applicantId),
        refetchInterval,
        queryFn: async () => {
            return await getApplicantVendorStatus(applicantId);
        },
    });

export const useApplicantDocumentsQuery = ({
    applicantId,
    enabled,
}: GetApplicantQueryProps): UseQueryResult<Document[]> =>
    useQuery({
        queryKey: ['applicant-documents', applicantId],
        staleTime: FOCUS_STALE_TIME_MS,
        enabled: Boolean(applicantId) && enabled,
        queryFn: async () => {
            return await getApplicantDocuments(applicantId);
        },
    });

export const useApplicantMissingDocumentsQuery = ({
    applicantId,
}: GetApplicantQueryProps): UseQueryResult<MissingDocumentsResponse> =>
    useQuery({
        queryKey: ['applicant-missing-documents', applicantId],
        staleTime: MISSING_DOCUMENTS_STALE_TIME_MS,
        enabled: Boolean(applicantId),
        queryFn: async () => {
            return await getApplicantMissingDocuments(applicantId);
        },
    });

export const useApplicantsQuery = (filters: GetApplicantsFilter) =>
    useQuery({
        queryKey: ['applicants', filters],
        refetchOnWindowFocus: false,
        // Failures are mostly timeouts, and a retry re-runs the slow query; the error state offers Retry.
        retry: false,
        queryFn: async () => {
            return await getApplicants(filters);
        },
    });

export const useAssigneesQuery = () =>
    useQuery({
        queryKey: ['assignees'],
        staleTime: FOCUS_STALE_TIME_MS,
        queryFn: async () => {
            return await getAssignees();
        },
    });

export const useApplicantTagsQuery = ({
    applicantId,
}: GetApplicantQueryProps): UseQueryResult<ApplicantTagResponse[]> =>
    useQuery({
        queryKey: ['applicant-tags', applicantId],
        staleTime: FOCUS_STALE_TIME_MS,
        enabled: Boolean(applicantId),
        queryFn: async () => {
            return await getApplicantTags(applicantId);
        },
    });

export const useApplicantReasonCodesQuery = ({
    applicantId,
}: GetApplicantQueryProps): UseQueryResult<ApplicantReasonCodeResponse[]> =>
    useQuery({
        queryKey: ['applicant-reason-codes', applicantId],
        staleTime: FOCUS_STALE_TIME_MS,
        enabled: Boolean(applicantId),
        queryFn: async () => {
            return await getApplicantReasonCodes(applicantId);
        },
    });

export const useApplicantNextStepsQuery = ({
    applicantId,
    enabled = false,
}: GetApplicantQueryProps): UseQueryResult<PartnersNextStepResponse, AxiosError> =>
    useQuery({
        queryKey: ['applicant-next-steps', applicantId],
        staleTime: FOCUS_STALE_TIME_MS,
        enabled: Boolean(applicantId) && enabled,
        queryFn: async () => {
            return await getApplicantNextSteps(applicantId);
        },
    });

export const useApplicantWatchlistMatchesQuery = ({
    applicantId,
}: GetApplicantQueryProps): UseQueryResult<WatchlistMatchesResponse[]> =>
    useQuery({
        queryKey: ['applicant-watchlist-matches', applicantId],
        staleTime: FOCUS_STALE_TIME_MS,
        enabled: Boolean(applicantId),
        queryFn: async () => {
            return await getApplicantWatchlistMatches(applicantId);
        },
    });

export const useApplicantRelatedAccountsQuery = ({
    applicantId,
}: GetApplicantQueryProps): UseQueryResult<RelatedAccountsResponse, AxiosError> =>
    useQuery({
        queryKey: ['applicant-related-accounts', applicantId],
        enabled: Boolean(applicantId),
        // Related accounts rarely change while a reviewer works an applicant; without this every window focus refetched them.
        staleTime: 60_000,
        // The server already returns partial results when one lookup fails, so a retry rarely helps; each section offers Retry.
        retry: false,
        queryFn: async () => {
            return await getApplicantRelatedAccounts(applicantId);
        },
    });

export const useApplicantSubmissionsQuery = ({
    applicantId,
    isEnabled = true,
}: GetApplicantQueryProps & {
    isEnabled?: boolean;
}): UseQueryResult<ApplicantSubmissionSummary[], AxiosError> =>
    useQuery({
        queryKey: ['applicant-submissions', applicantId],
        staleTime: FOCUS_STALE_TIME_MS,
        // The tab offers Retry, so a failure shows at once instead of after the backoff.
        retry: false,
        enabled: isEnabled && Boolean(applicantId),
        queryFn: async () => {
            return await getApplicantSubmissions(applicantId);
        },
    });

export const useApplicantSubmissionQuery = ({
    applicantId,
    applicantSubmissionId,
}: {
    applicantId: string;
    applicantSubmissionId: string | null;
}): UseQueryResult<Applicant, AxiosError> =>
    useQuery({
        queryKey: ['applicant-submission', applicantId, applicantSubmissionId],
        enabled: Boolean(applicantId) && Boolean(applicantSubmissionId),
        // A recorded submission never changes.
        staleTime: Infinity,
        // A payload that fails to decode fails every time; the tab offers Retry for the rest.
        retry: false,
        queryFn: async () => {
            return await getApplicantSubmission(applicantId, applicantSubmissionId ?? '');
        },
    });

export const useApplicantPointOfContactsQuery = ({
    applicantId,
}: GetApplicantQueryProps): UseQueryResult<PointOfContact[]> =>
    useQuery({
        queryKey: ['applicant-point-of-contacts', applicantId],
        staleTime: FOCUS_STALE_TIME_MS,
        enabled: Boolean(applicantId),
        queryFn: async () => {
            return await getApplicantPointOfContacts(applicantId);
        },
    });

export const useOrpUserProfilesQuery = (enabled = true): UseQueryResult<OrpUserProfile[]> =>
    useQuery({
        queryKey: ['orp-user-profiles'],
        enabled,
        // The reviewer roster barely changes during a session; without this every window focus refetched it.
        staleTime: 5 * 60 * 1000,
        queryFn: getOrpUserProfiles,
    });

export const useRecentNeedsMoreInfoSentQuery = (): UseQueryResult<RecentNeedsMoreInfoSentRow[]> =>
    useQuery({
        queryKey: ['recent-needs-more-info-sent'],
        staleTime: FOCUS_STALE_TIME_MS,
        queryFn: getRecentNeedsMoreInfoSent,
        retry: false,
        throwOnError: false,
    });
